import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PositionCanvasEditor from "./PositionCanvasEditor";
import SpecsModelAutocompleteField from "./components/editors/SpecsModelAutocompleteField";
import SpecsModelImageUrlField from "./components/editors/SpecsModelImageUrlField";
import SpecsModelNumberField from "./components/editors/SpecsModelNumberField";
import SpecsModelSelectField from "./components/editors/SpecsModelSelectField";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useSpecsModel, useSpecsModelSelector } from "./SpecsModelContext";
import {
  createDefaultPositionSideForImage,
  getAssetPreviewUrl,
  loadImageDimensions,
} from "./specsEditorUtils";
import {
  getSelectedPositionForVersion,
  getSelectedPositionSideSnapshots,
  mutateSelectedPositionForVersion,
  syncLockedSideDimension,
} from "./positionEditorModel";

const ALIGNMENT_X_OPTIONS = [
  { value: "l", label: "Left" },
  { value: "c", label: "Center" },
  { value: "r", label: "Right" },
];
const ALIGNMENT_Y_OPTIONS = [
  { value: "t", label: "Top" },
  { value: "c", label: "Center" },
  { value: "b", label: "Bottom" },
];
const COMMON_SIDE_NAME_OPTIONS = ["front", "back", "left", "right", "inside"];
const SIDE_FIELDS = [
  ["X", "x"],
  ["Y", "y"],
  ["Width", "w"],
  ["Height", "h"],
  ["Rotation", "rotation"],
  ["Angle du plan", "planeAngle"],
] as const;

function SpecsPositionSideCard({
  side,
  sideIndex,
  sideNameOptions,
  compositeUrl,
  backCompositeUrl,
  allowIndependentScaling,
  selectedPositionPath,
}: {
  side: any;
  sideIndex: number;
  sideNameOptions: string[];
  compositeUrl: string | null;
  backCompositeUrl: string | null;
  allowIndependentScaling: boolean;
  selectedPositionPath: string | null;
}) {
  const sideDebugPath = selectedPositionPath
    ? `${selectedPositionPath}.generator.sides[${sideIndex}]`
    : null;
  const specsModel = useSpecsModel();
  const {
    editor: {
      identity: { productVersion },
    },
    selectedPositionId,
  } = useSpecsEditorContextValue();

  const mutateSideAtIndex = React.useCallback(
    (mutator: (side: any) => void) => {
      mutateSelectedPositionForVersion(
        specsModel,
        productVersion,
        selectedPositionId,
        (position) => {
          const currentGenerator = position.generator ?? { sides: [] };
          const currentSides = currentGenerator.sides ?? [];
          const currentSide = currentSides[sideIndex];
          if (!currentSide) {
            return;
          }

          const draftSide = { ...currentSide };
          mutator(draftSide);

          position.generator = {
            ...currentGenerator,
            sides: currentSides.map((side: any, index: number) =>
              index === sideIndex ? draftSide : side
            ),
          };
        }
      );
    },
    [productVersion, selectedPositionId, sideIndex, specsModel]
  );
  const removeSide = React.useCallback(() => {
    mutateSelectedPositionForVersion(
      specsModel,
      productVersion,
      selectedPositionId,
      (position) => {
        const currentGenerator = position.generator ?? { sides: [] };
        const currentSides = currentGenerator.sides ?? [];
        position.generator = {
          ...currentGenerator,
          sides: currentSides.filter(
            (_side: any, index: number) => index !== sideIndex
          ),
        };
      }
    );
  }, [productVersion, selectedPositionId, sideIndex, specsModel]);

  const previewUrl =
    side.name === "back"
      ? backCompositeUrl || compositeUrl
      : compositeUrl || backCompositeUrl;

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%", pr: 1 }}
        >
          <Typography variant="subtitle2">Side {sideIndex + 1}</Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              removeSide();
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pb: 2.5 }}>
        {!previewUrl ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Upload or import garment imagery to position this box visually.
          </Alert>
        ) : (
          <Box sx={{ mb: 1.5 }}>
            <PositionCanvasEditor
              imageUrl={previewUrl}
              side={side}
              allowResize={allowIndependentScaling}
              onChange={(nextSide) =>
                mutateSideAtIndex((currentSide) => {
                  Object.assign(currentSide, nextSide);
                })
              }
            />
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 1.5,
          }}
        >
          <SpecsModelAutocompleteField
            label="Nom du côté"
            debugPath={sideDebugPath ? `${sideDebugPath}.name` : undefined}
            options={sideNameOptions}
            placeholder="front"
            selector={(model) =>
              getSelectedPositionForVersion(
                model,
                productVersion,
                selectedPositionId
              )?.generator?.sides?.[sideIndex]?.name ?? ""
            }
            setValue={(_model, nextValue) =>
              mutateSideAtIndex((currentSide) => {
                currentSide.name = nextValue;
              })
            }
          />
          <SpecsModelNumberField
            label="Z Index"
            debugPath={sideDebugPath ? `${sideDebugPath}.zIndex` : undefined}
            size="small"
            selector={(model) =>
              getSelectedPositionForVersion(
                model,
                productVersion,
                selectedPositionId
              )?.generator?.sides?.[sideIndex]?.zIndex ?? 0
            }
            setValue={(_model, nextValue) =>
              mutateSideAtIndex((currentSide) => {
                currentSide.zIndex = nextValue;
              })
            }
          />
          <SpecsModelSelectField
            label="Align X"
            debugPath={
              sideDebugPath ? `${sideDebugPath}.alignmentX` : undefined
            }
            options={ALIGNMENT_X_OPTIONS}
            selector={(model) =>
              getSelectedPositionForVersion(
                model,
                productVersion,
                selectedPositionId
              )?.generator?.sides?.[sideIndex]?.alignmentX ?? "c"
            }
            setValue={(_model, nextValue) =>
              mutateSideAtIndex((currentSide) => {
                currentSide.alignmentX = nextValue;
              })
            }
          />
          <SpecsModelSelectField
            label="Align Y"
            debugPath={
              sideDebugPath ? `${sideDebugPath}.alignmentY` : undefined
            }
            options={ALIGNMENT_Y_OPTIONS}
            selector={(model) =>
              getSelectedPositionForVersion(
                model,
                productVersion,
                selectedPositionId
              )?.generator?.sides?.[sideIndex]?.alignmentY ?? "t"
            }
            setValue={(_model, nextValue) =>
              mutateSideAtIndex((currentSide) => {
                currentSide.alignmentY = nextValue;
              })
            }
          />
          {SIDE_FIELDS.map(([label, key]) => (
            <SpecsModelNumberField
              key={`${sideIndex}-${key}`}
              label={label}
              debugPath={sideDebugPath ? `${sideDebugPath}.${key}` : undefined}
              size="small"
              disabled={
                !allowIndependentScaling && (key === "w" || key === "h")
              }
              selector={(model) =>
                getSelectedPositionForVersion(
                  model,
                  productVersion,
                  selectedPositionId
                )?.generator?.sides?.[sideIndex]?.[key] ?? 0
              }
              setValue={(_model, nextValue) =>
                mutateSideAtIndex((currentSide) => {
                  currentSide[key] = nextValue;
                })
              }
            />
          ))}
          <SpecsModelImageUrlField
            label="Mask URL"
            debugPath={sideDebugPath ? `${sideDebugPath}.maskUrl` : undefined}
            size="small"
            uploadContext={{
              fieldPath: `positions.${selectedPositionId || "selected"}.generator.sides.${sideIndex}.maskUrl`,
            }}
            selector={(model) =>
              getSelectedPositionForVersion(
                model,
                productVersion,
                selectedPositionId
              )?.generator?.sides?.[sideIndex]?.maskUrl ?? ""
            }
            setValue={(_model, nextValue) =>
              mutateSideAtIndex((currentSide) => {
                if (nextValue) {
                  currentSide.maskUrl = nextValue;
                } else {
                  delete currentSide.maskUrl;
                }
              })
            }
            previewSrc={
              getAssetPreviewUrl(
                side as Record<string, any>,
                "maskUrl",
                side.maskUrl
              ) ?? undefined
            }
            addLeftPreview
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default function SpecsPositionSidesSection() {
  const specsModel = useSpecsModel();
  const {
    editor: {
      identity: { productVersion },
    },
    globalPreviewComposite,
    selectedPositionId,
  } = useSpecsEditorContextValue();
  const compositeUrl = globalPreviewComposite.front.url;
  const backCompositeUrl = globalPreviewComposite.back.url;
  const positionSides = useSpecsModelSelector((model) =>
    getSelectedPositionSideSnapshots(model, productVersion, selectedPositionId)
  );
  const selectedPositionPath = useSpecsModelSelector((model) => {
    const versionIndex = model.product.versions.findIndex(
      (version) => version.name === productVersion
    );
    const version = model.product.versions[versionIndex];

    if (!version) {
      return null;
    }

    const positionIndex = version.positions.findIndex(
      (position: any) => position?.id === selectedPositionId
    );

    if (positionIndex === -1) {
      return null;
    }

    return `window.specs.model.product.versions[${versionIndex}].positions[${positionIndex}]`;
  });
  const allowIndependentScaling = useSpecsModelSelector(
    (model) =>
      getSelectedPositionForVersion(model, productVersion, selectedPositionId)
        ?.generator?.allowIndependentScaling === true
  );
  const setAllowIndependentScaling = React.useCallback(
    (nextValue: boolean) => {
      mutateSelectedPositionForVersion(
        specsModel,
        productVersion,
        selectedPositionId,
        (position, version) => {
          const currentGenerator = position.generator ?? { sides: [] };
          position.generator = {
            ...currentGenerator,
            allowIndependentScaling: nextValue,
          };
          if (!nextValue) {
            syncLockedSideDimension(
              version,
              position,
              "w",
              position.designState.maxWidthInches
            );
            syncLockedSideDimension(
              version,
              position,
              "h",
              position.designState.maxHeightInches
            );
          }
        }
      );
    },
    [productVersion, selectedPositionId, specsModel]
  );
  const sideNameOptions = useSpecsModelSelector((model) =>
    Array.from(
      new Set(
        [
          ...COMMON_SIDE_NAME_OPTIONS,
          ...(model.getVersion(productVersion)?.getPositions() ?? []).flatMap(
            (position: any) =>
              (position.generator?.sides ?? []).map((side: any) => side.name)
          ),
        ].filter(Boolean)
      )
    ).sort()
  );
  const addSide = React.useCallback(async () => {
    const sideName = compositeUrl ? "front" : "back";
    const imageSize = await loadImageDimensions(
      sideName === "back" ? backCompositeUrl : compositeUrl
    );

    mutateSelectedPositionForVersion(
      specsModel,
      productVersion,
      selectedPositionId,
      (position) => {
        const currentGenerator = position.generator ?? { sides: [] };
        const currentSides = currentGenerator.sides ?? [];
        position.generator = {
          ...currentGenerator,
          sides: [
            ...currentSides,
            createDefaultPositionSideForImage({
              sideName,
              imageSize,
            }),
          ],
        };
      }
    );
  }, [
    backCompositeUrl,
    compositeUrl,
    productVersion,
    selectedPositionId,
    specsModel,
  ]);

  return (
    <Stack spacing={1.5} sx={{ pb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Sides</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addSide}>
          Add side
        </Button>
      </Stack>

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={allowIndependentScaling}
            onChange={(event) =>
              setAllowIndependentScaling(event.target.checked)
            }
          />
        }
        label="Allow independent scaling"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1.5,
          alignItems: "start",
        }}
      >
        {positionSides.map((side: any, sideIndex: number) => (
          <SpecsPositionSideCard
            key={`${side.name}-${sideIndex}`}
            side={side}
            sideIndex={sideIndex}
            sideNameOptions={sideNameOptions}
            compositeUrl={compositeUrl}
            backCompositeUrl={backCompositeUrl}
            allowIndependentScaling={allowIndependentScaling}
            selectedPositionPath={selectedPositionPath}
          />
        ))}
      </Box>
    </Stack>
  );
}
